import { z } from 'zod/v3'
import {
  EmployeeDTOSchema,
  EmployeeMyComplianceDashboardDTOSchema,
} from './employee.dto'
import { WorkflowParamSchema } from './workflow.dto'

export const EmployeePortalModuleKeySchema = z.string().trim().min(1).max(100)
export type EmployeePortalModuleKey = z.infer<
  typeof EmployeePortalModuleKeySchema
>

export const EmployeePortalDestinationSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('EXTERNAL_LINK'),
    url: z.string().url(),
  }),
  z.object({
    type: z.literal('PROCEDURE'),
    procedureId: z.string().optional(),
  }),
])

export type EmployeePortalDestination = z.infer<
  typeof EmployeePortalDestinationSchema
>

export const EmployeePortalSettingsRowDTOSchema = z.object({
  key: EmployeePortalModuleKeySchema,
  title: z.string(),
  enabled: z.boolean(),
  destination: EmployeePortalDestinationSchema.nullable(),
  procedure: z
    .object({
      id: z.string(),
      title: z.string(),
      published: z.boolean(),
    })
    .nullable(),
})

export type EmployeePortalSettingsRowDTO = z.infer<
  typeof EmployeePortalSettingsRowDTOSchema
>

export const EmployeePortalActionDTOSchema = z.object({
  key: EmployeePortalModuleKeySchema,
  title: z.string(),
  destination: EmployeePortalDestinationSchema,
  params: z.record(WorkflowParamSchema).optional(),
  initialInputs: z.record(z.unknown()).optional(),
})

export type EmployeePortalActionDTO = z.infer<
  typeof EmployeePortalActionDTOSchema
>

export const StartEmployeePortalActionResultDTOSchema = z.object({
  executionId: z.string(),
  taskId: z.string(),
})

export const EmployeeCenterOverviewDTOSchema = z.object({
  totalEmployees: z.number(),
  linkedEmployees: z.number(),
  pendingInvitations: z.number(),
  employeesWithPendingItems: z.number(),
})

export type EmployeeCenterOverviewDTO = z.infer<
  typeof EmployeeCenterOverviewDTOSchema
>

export const EmployeeCenterEligibleEmployeeDTOSchema = z.object({
  employeeId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
  status: z.string(),
  userId: z.string().nullable(),
  hasPendingInvitation: z.boolean(),
  workspaceMemberId: z.string().nullable(),
})

export type EmployeeCenterEligibleEmployeeDTO = z.infer<
  typeof EmployeeCenterEligibleEmployeeDTOSchema
>

export const EmployeeCenterPendingInvitationDTOSchema = z.object({
  invitationId: z.string(),
  employeeId: z.string(),
  email: z.string(),
  employeeName: z.string(),
  sentAt: z.coerce.date(),
  expiresAt: z.coerce.date(),
})

export type EmployeeCenterPendingInvitationDTO = z.infer<
  typeof EmployeeCenterPendingInvitationDTOSchema
>

export const EmployeeCenterInviteResultDTOSchema = z.object({
  success: z.literal(true),
  invitationId: z.string(),
})

export type EmployeeCenterInviteResultDTO = z.infer<
  typeof EmployeeCenterInviteResultDTOSchema
>

export const EmployeeCenterMyDashboardDTOSchema =
  EmployeeMyComplianceDashboardDTOSchema

export type EmployeeCenterMyDashboardDTO = z.infer<
  typeof EmployeeCenterMyDashboardDTOSchema
>

export const EmployeeCenterEmployeeSummaryDTOSchema = EmployeeDTOSchema.pick({
  id: true,
  firstName: true,
  lastName: true,
  email: true,
  title: true,
  department: true,
  status: true,
  userId: true,
})

export type EmployeeCenterEmployeeSummaryDTO = z.infer<
  typeof EmployeeCenterEmployeeSummaryDTOSchema
>
